/* src/pages/LawFirmITServices.js */

import React from "react";
import PageSection from "../components/PageSection";
import { Helmet } from "react-helmet-async";
import styles from "./Pages.module.css";
import { Link } from "react-router-dom";

function LawFirmITServices() {
  const faqs = [
    {
      question: "Do you work with specific case management software like Clio or MyCase?",
      answer:
        "Yes. I've worked with firms using Clio, MyCase, PracticePanther, and other common legal practice management platforms. I handle the IT side, network setup, workstation performance, backups, and secure access. For software-specific support, you'd work with the vendor, but I make sure your systems are set up to run their software reliably.",
    },
    {
      question: "How do you handle client file security?",
      answer:
        "Client files require secure storage and controlled access. I implement access controls so only authorized attorneys and staff can access specific client files, encrypted backups so files are recoverable if something fails, and basic audit practices so you know who accessed what. For full bar compliance and data retention requirements, you'll want to work with your firm's compliance counsel.",
    },
    {
      question: "Can you set up remote access for attorneys working outside the office?",
      answer:
        "Yes. I handle VPN setup, secure remote desktop connections, and mobile device management so attorneys can work securely from home or the courthouse without compromising client data security.",
    },
    {
      question: "What if a client file gets accidentally deleted or corrupted?",
      answer:
        "If you have proper backups in place, it's a minor inconvenience, I restore from backup and you're back to work. If you don't have backups yet, the IT Health Check will show you exactly where the risk is and what needs to change.",
    },
  ];

  return (
    <PageSection title="IT Support for Law Firms in Shreveport-Bossier" cta faqs={faqs}>
      <Helmet>
        <title>IT Support for Law Firms in Shreveport-Bossier | Tharpe Industries</title>
        <meta
          name="description"
          content="IT support for law firms in Shreveport and Bossier City. Secure client files, case management systems, and firm communications. Dedicated local technician, no contracts."
        />
        <meta
          name="keywords"
          content="law firm IT support Shreveport, legal practice management IT, Clio support, MyCase support, law office network security"
        />
        <link rel="canonical" href="https://tharpeindustries.com/it-support-law-firms" />
      </Helmet>

      <div className={styles.contentContainer}>
        <div className={styles.textContent}>
          <h2>Keep client files, case management software, and your firm's communications secure and accessible.</h2>
          <div className={styles.heroQuickCta}>
            <Link to="/it-health-check" className={styles.heroQuickCtaBtn}>Book an IT Health Check, $149 →</Link>
          </div>
          <p>
            Law firms depend on secure IT infrastructure. Client files, case management systems, attorney communications, and document storage all need to work reliably without compromising confidentiality. A system failure doesn't just slow you down, it raises compliance questions, puts client data at risk, and creates legal exposure.
          </p>
          <p>
            Austin Tharpe helps Shreveport-Bossier law firms keep their systems secure and running. I handle workstation maintenance, secure backups, remote access setup, and the IT basics of client data protection.
          </p>

          <h3>Why law firms have unique IT needs</h3>
          <ul>
            <li><strong>Client confidentiality is non-negotiable:</strong> Attorney-client privilege extends to how you store and protect files. Client files require secure storage, controlled access (only authorized attorneys can see specific files), and backups that keep data safe if something fails.</li>
            <li><strong>Case management software requires reliable systems:</strong> Clio, MyCase, PracticePanther, and other practice management platforms need stable, well-maintained workstations. Slow computers or system crashes mean lost billable hours.</li>
            <li><strong>Document storage and backups are critical:</strong> Law practices are document-heavy. Years of client files, case materials, and discovery documents need to be stored securely, accessed reliably, and backed up so nothing is lost.</li>
            <li><strong>Remote work needs secure access:</strong> Attorneys work from home, the courthouse, and client offices. They need secure remote access to firm systems without putting client data at risk. That means VPN, secure connections, and proper authentication.</li>
          </ul>

          <h3>What Tharpe Industries handles for law firms</h3>
          <ul>
            <li><strong>Workstation maintenance and performance:</strong> Slow case management software? System crashes? I handle diagnostics and fixes so your practice management platform runs at full speed and your attorneys can work efficiently.</li>
            <li><strong>Secure file storage and backup:</strong> Client files need to be backed up automatically, stored securely with encryption, and accessible only to authorized staff. If a workstation fails or data is lost, you can restore from backup without losing anything.</li>
            <li><strong>Remote access setup:</strong> I implement VPN, secure remote desktop connections, and mobile access so attorneys can work securely from anywhere without compromising client data security.</li>
            <li><strong>Email security and spam filtering:</strong> Client communications go over email. I set up email security, spam filtering, and basic phishing protection so sensitive communications stay secure.</li>
            <li><strong>New computer setup and software installation:</strong> Need a new workstation in the office or a laptop for an attorney? I handle hardware setup, software installation, and integration with your practice management system.</li>
          </ul>

          <h3>I handle IT infrastructure. You need compliance counsel for bar requirements.</h3>
          <p>
            Tharpe Industries handles the IT side of client data protection, secure backups, access controls, encryption, and secure remote access. For bar compliance requirements, data retention policies, and attorney conduct rules around technology, you'll want to consult with your firm's compliance counsel or a legal technology compliance specialist. Think of it this way: I make sure your systems are secure and reliable; they make sure your policies and documentation meet bar requirements.
          </p>

          <div className={styles.ctaContainer}>
            <p><strong>Ready to see where your firm's IT actually stands?</strong></p>
            <p className={styles.emergencyText}>Start with the $149 IT Health Check. I'll review your current setup, case management software performance, backup security, and remote access configuration, then give you a clear picture of what's working and what needs attention.</p>
            <Link to="/it-health-check" className={styles.ctaButton}>Start with the IT Health Check</Link>
          </div>

          <div className={styles.secondaryCta}>
            <p>Have questions about your firm's specific setup? <Link to="/contact-us">Send me a message</Link> and I'll help you think through what you need.</p>
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted, #9ca3af)', marginBottom: '0.75rem' }}>I also serve other local businesses:</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem' }}>
              <li><Link to="/it-support-medical-offices" style={{ fontSize: '0.9rem' }}>Medical Offices</Link></li>
              <li><Link to="/it-support-dental-offices" style={{ fontSize: '0.9rem' }}>Dental Practices</Link></li>
              <li><Link to="/it-support-accounting-firms" style={{ fontSize: '0.9rem' }}>Accounting Firms</Link></li>
              <li><Link to="/it-support-contractors" style={{ fontSize: '0.9rem' }}>Contractors & Trades</Link></li>
              <li><Link to="/it-support-restaurants" style={{ fontSize: '0.9rem' }}>Restaurants</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </PageSection>
  );
}

export default LawFirmITServices;
